/**
 * The half-typed question and the topic chip, per conversation, persisted.
 *
 * A2 kept both in its app store so that closing the trainer and coming back
 * found them where they were (A2 `coach.tsx` :86-96, "the half-typed draft
 * and the topic survive closing"). Here they outlive a restart too, through
 * `preferences` (`src/native`), and they are keyed by conversation: a draft
 * written in one conversation must not appear in another (T44 made there be
 * more than one).
 *
 * - `text` is what the input shows. `topic` is the "Sobre: <nome>" chip,
 *   which is NOT text: the composer stays empty so the mic stays reachable,
 *   and the prefix is added only on send (A2 :215, `composeQuestion`).
 * - At most {@link MAX_ENTRIES} conversations are kept, newest first; an
 *   empty entry is dropped rather than stored.
 * - Hydration is asynchronous (Preferences is), so a `?ask=` acted on before
 *   it finishes must win over the stored copy: `merge` lets the in-memory
 *   entry beat the persisted one for the same conversation.
 *
 * T62's mic writes its transcript through {@link appendComposerText}; there
 * is no second draft anywhere.
 */
import { useStore } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import { createStore } from "zustand/vanilla";
import { preferences } from "../../native";

export const COMPOSER_STORAGE_KEY = "mypocketgym.coach.composer";
export const MAX_ENTRIES = 20;

export interface ComposerEntry {
  /** Epoch ms of the last edit; the oldest entries go first. */
  readonly at: number;
  readonly text: string;
  readonly topic: string | null;
}

export interface ComposerState {
  readonly entries: Readonly<Record<string, ComposerEntry>>;
}

const EMPTY: ComposerEntry = { at: 0, text: "", topic: null };

/**
 * `entries` with `id` set to `next` (or removed when it is empty), trimmed to
 * the newest {@link MAX_ENTRIES}. Pure, for the test.
 */
export function withEntry(
  entries: Readonly<Record<string, ComposerEntry>>,
  id: string,
  next: ComposerEntry
): Record<string, ComposerEntry> {
  const rest: Record<string, ComposerEntry> = { ...entries };
  delete rest[id];
  if (next.text === "" && next.topic === null) {
    return rest;
  }
  const kept = Object.entries(rest)
    .sort(([, a], [, b]) => b.at - a.at)
    .slice(0, MAX_ENTRIES - 1);
  return Object.fromEntries([[id, next], ...kept]);
}

/** The persisted entries under the in-memory ones: memory wins per conversation. */
export function mergeEntries(
  persisted: unknown,
  current: ComposerState
): ComposerState {
  const stored =
    typeof persisted === "object" && persisted !== null
      ? (persisted as Partial<ComposerState>).entries
      : undefined;
  return {
    ...current,
    entries: { ...(stored ?? {}), ...current.entries },
  };
}

export const composerStore = createStore<ComposerState>()(
  persist(() => ({ entries: {} }), {
    merge: mergeEntries,
    name: COMPOSER_STORAGE_KEY,
    storage: createJSONStorage(() => ({
      getItem: (key) => preferences.get(key),
      removeItem: (key) => preferences.remove(key),
      setItem: (key, value) => preferences.set(key, value),
    })),
    version: 1,
  })
);

function update(
  id: string,
  change: (entry: ComposerEntry) => Omit<ComposerEntry, "at">
): void {
  composerStore.setState((state) => {
    const current = state.entries[id] ?? EMPTY;
    return {
      entries: withEntry(state.entries, id, {
        ...change(current),
        at: Date.now(),
      }),
    };
  });
}

export function setComposerText(id: string, text: string): void {
  update(id, (entry) => ({ text, topic: entry.topic }));
}

/** A transcript lands after what is already typed, separated by a space. */
export function appendComposerText(id: string, text: string): void {
  update(id, (entry) => {
    const typed = entry.text.trim();
    return {
      text: typed === "" ? text : `${typed} ${text}`,
      topic: entry.topic,
    };
  });
}

export function setComposerTopic(id: string, topic: string | null): void {
  update(id, (entry) => ({ text: entry.text, topic }));
}

/** `?ask=`: the whole question in the composer, and no topic (A2 :152-165). */
export function startWithQuestion(id: string, text: string): void {
  update(id, () => ({ text, topic: null }));
}

/** `?about=`: the topic chip, and an empty composer (A2 :167-179). */
export function startWithTopic(id: string, topic: string): void {
  update(id, () => ({ text: "", topic }));
}

export function clearComposer(id: string): void {
  update(id, () => ({ text: "", topic: null }));
}

/**
 * Every draft, in every conversation, for the account that just left (T35's
 * cleanup, `data/account-cleanup.ts`). A half-written question to the trainer
 * is the previous person's, and the conversations it is keyed by are theirs
 * too.
 *
 * `persist` writes the empty map after the `set`, asynchronously — the store
 * is already empty for the screen by the time this returns.
 */
export function clearAllComposers(): void {
  composerStore.setState({ entries: {} });
}

export function useComposer(id: string): Omit<ComposerEntry, "at"> {
  return useStore(
    composerStore,
    useShallow((state) => {
      const entry = state.entries[id] ?? EMPTY;
      return { text: entry.text, topic: entry.topic };
    })
  );
}
